"use client"
import { useEffect, useState } from "react"
import Image from "next/image"
import { Typography } from "@/components/ui/typography"
import VideoPlayer from "../Video"
import Content from "../ui/Content"
import { TContentCardProps } from "@/types/base"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import {
  BLOCKS,
  INLINES,
  Document as ContentfulDocument,
} from "@contentful/rich-text-types"
import { calculateReadTime, getImageDimensions } from "@/lib/utils"
import { parse } from "path"

export const ReadMoreDialog = ({
  image,
  slug,
  headline,
  articleText,
  videoUrl,
  updatedAt,
  trigger,
}: TContentCardProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [imageLoaded, setImageLoaded] = useState(false)

  const hasImage = Boolean(image)
  const hasVideo = Boolean(videoUrl)
  const hasReadMoreText = Boolean(articleText)

  const readTime = calculateReadTime(articleText, image)

  // Helper function to generate clean article name for URLs
  const generateArticleName = (title: string | ContentfulDocument) => {
    if (!title) return ""

    // Handle Contentful Document type by converting to string
    const titleString = typeof title === "string" ? title : title.toString()

    return titleString
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-") // Convert spaces and special chars to dashes
      .replace(/^-+|-+$/g, "") // Remove leading/trailing dashes
      .substring(0, 50) // Limit length to avoid extremely long URLs
  }

  // Update URL when modal opens with slug + article name
  const handleOpenChange = (open: boolean) => {
    if (open === isOpen) return // Prevent unnecessary updates

    // Update modal state immediately for responsive UI
    setIsOpen(open)

    if (open && slug) {
      // Create URL with slug + article name for better SEO and UX
      const articleName = generateArticleName(headline)
      const newUrl = `/${slug}/${articleName}`
      window.history.pushState({}, "", newUrl)
    } else if (!open) {
      // Return to root path
      window.history.pushState({}, "", "/")
    }
  }

  // Dedicated close function
  const handleClose = () => {
    console.log("Closing modal") // Debug log
    setIsOpen(false)
    setImageLoaded(false)
    // Return to root path
    window.history.pushState({}, "", "/")
  }

  // Check if this article should be open based on slug + article name URL path
  useEffect(() => {
    const currentPath = window.location.pathname
    const pathSegments = currentPath.split("/").filter(Boolean)
    const articleName = generateArticleName(headline)

    // Check if URL matches our slug (first segment should match slug)
    if (
      pathSegments.length > 0 &&
      slug &&
      pathSegments[0] === slug &&
      pathSegments[1] === articleName &&
      !isOpen
    ) {
      // Small delay to ensure smooth transition
      requestAnimationFrame(() => setIsOpen(true))
    } else if (pathSegments.length === 0 || pathSegments[0] !== slug) {
      // Close modal if we're not on the article path
      if (isOpen) {
        setIsOpen(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isOpen]) // headline is used in generateArticleName but is stable

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const currentPath = window.location.pathname
      const pathSegments = currentPath.split("/").filter(Boolean)

      // Check if URL still matches our slug (first segment should match slug)
      if (pathSegments.length === 0 || pathSegments[0] !== slug) {
        setIsOpen(false)
      }
    }

    window.addEventListener("popstate", handlePopState)
    return () => window.removeEventListener("popstate", handlePopState)
  }, [slug, isOpen])

  useEffect(() => {
    if (!image) return
    getImageDimensions(image).then((dims) => {
      setDimensions((prev) =>
        prev.width === dims.width && prev.height === dims.height ? prev : dims,
      )
    })
  }, [image])

  const needsContainment = dimensions.height > 2000
  const imageClassName = needsContainment
    ? "w-auto object-contain max-h-[80vh]"
    : "w-full object-cover h-[357px]"

  return (
    <>
      {/* Always render the modal content but control visibility */}
      {isOpen && (
        <div
          className={cn(
            "fixed inset-0 z-50 transition-all duration-300 ease-out",
            isOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none",
          )}
        >
          {/* Custom backdrop with click outside functionality */}
          <div
            className="fixed inset-0 bg-background/80 backdrop-blur-[2px] transition-opacity duration-300"
            onClick={handleClose}
          />

          <div
            className={cn(
              "fixed left-[50%] top-[50%] z-50 grid w-full max-w-3xl max-h-[90vh] overflow-y-auto p-4 md:p-16 gap-6 bg-background shadow-lg",
              "transition-all duration-300 ease-out transform",
              "!rounded-none", // Override default rounded-none
              isOpen
                ? "opacity-100 scale-100 translate-x-[-50%] translate-y-[-50%]"
                : "opacity-0 scale-95 translate-x-[-50%] translate-y-[-50%]",
            )}
            onClick={(e) => e.stopPropagation()} // Prevent clicks inside modal from closing it
          >
            <button
              className="absolute top-4 right-4 w-6 h-6 ring-offset-0 text-muted-foreground focus:outline-none hover:opacity-70 transition-opacity"
              onClick={handleClose}
              type="button"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Visually hidden title for screen readers */}
            <div className="sr-only">
              {typeof headline === "string" ? headline : "Article"}
            </div>

            <div className="flex flex-col gap-3">
              <div className="space-y-1.5">
                <Content
                  slug={slug}
                  headline={headline}
                  updatedAt={updatedAt}
                  variant="headline"
                />
                <p className="text-xs font-medium text-muted-foreground">
                  {readTime}
                </p>
              </div>

              <div>
                {hasVideo && !hasReadMoreText ? (
                  <VideoPlayer url={videoUrl} />
                ) : (
                  hasImage && (
                    <div
                      className={cn(
                        "relative w-full flex justify-center",
                        !imageLoaded &&
                          "bg-slate-600 animate-pulse width-[360px] height-[240px] md:width-[600px] md:height-[400px] lg:width-[625px] lg:height-[425px]",
                      )}
                    >
                      <Image
                        src={image}
                        alt={headline as string}
                        width={0}
                        height={0}
                        quality={100}
                        className={imageClassName}
                        loading="eager"
                        placeholder="empty"
                        onLoad={() => setImageLoaded(true)}
                        sizes="100vw"
                      />
                    </div>
                  )
                )}
              </div>
            </div>

            {articleText && (
              <Typography
                variant="body"
                className="text-muted-foreground leading-6"
                as="div"
              >
                {documentToReactComponents(articleText, options)}
              </Typography>
            )}
          </div>
        </div>
      )}

      {/* Trigger button */}
      <div onClick={handleOpenChange.bind(null, true)}>{trigger}</div>
    </>
  )
}

const options = {
  renderNode: {
    [BLOCKS.HEADING_1]: (node, children) => (
      <h1 className="text-3xl leading-tight font-bold pt-8 pb-1">{children}</h1>
    ),
    [BLOCKS.HEADING_2]: (node, children) => (
      <h2 className="text-2xl leading-snug font-semibold pt-6 pb-1">
        {children}
      </h2>
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <h3 className="text-large leading-normal font-semibold pb-5 pb-1">
        {children}
      </h3>
    ),
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <p className="text-base font-medium leading-normal py-1 [&:not(:first-child)]:pt-3">
        {children}
      </p>
    ),
    [BLOCKS.QUOTE]: (node, children) => (
      <div className="py-2">
        <blockquote className="text-base font-medium leading-normal border-l-4 border-current pl-3">
          {children}
        </blockquote>
      </div>
    ),
    [BLOCKS.HR]: () => <hr className="my-4 border-current" />,
    [BLOCKS.UL_LIST]: (node, children) => (
      <ul className="ml-6 py-4 list-disc">{children}</ul>
    ),
    [BLOCKS.OL_LIST]: (node, children) => (
      <ol className="ml-6 py-4 list-decimal">{children}</ol>
    ),
    [BLOCKS.LIST_ITEM]: (node, children) => (
      <li className="text-base font-medium">{children}</li>
    ),
    [INLINES.HYPERLINK]: (node, children) => {
      const url = node.data.uri
      return (
        <a
          href={url}
          className="underline hover:text-foreground transition-colors"
          target="_blank"
          rel="noopener noreferrer"
        >
          {children}
        </a>
      )
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const url = node?.data?.target?.startsWith("//")
        ? `https:${node.data.target}`
        : node?.data?.target

      if (url) {
        const fileName = parse(new URL(url).pathname).name ?? "Embedded Asset"
        return (
          <div className="relative w-auto flex justify-center my-2 mx-16">
            <Image
              src={url}
              alt={fileName}
              title={fileName}
              width={0}
              height={0}
              quality={100}
              className="w-full object-cover h-[357px]"
              sizes="100vw"
            />
          </div>
        )
      }

      return null
    },
  },
}
